import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalHeader,
} from "../../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import ModalProfileHeader from "../../Common/ModalProfileHeader";
import { capitalizeFirstLetter } from "../../../helpers";

const WithdrawFundFromWallet = ({
    withdrawFundMutate,
    withdrawFundLoading,
    handleClose,
    selectedData,
}) => {
    const initialValues = {
        amount: "",
        remarks: "",
    };

    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const walletBalance = Number(
        selectedData?.balance ??
        selectedData?.walletBalance ??
        selectedData?.availableBalance ??
        0
    );

    const validationSchema = Yup.object({
        amount: Yup.number()
            .required("Amount is required")
            .typeError("Enter a valid amount")
            .moreThan(0, "Amount must be greater than 0")
            .max(
                walletBalance,
                `Amount cannot exceed wallet balance of ₹${walletBalance.toLocaleString(
                    "en-IN"
                )}`
            ),

        remarks: Yup.string()
            .max(250, "Remarks cannot exceed 250 characters"),
    });

    const handleSubmit = async (values) => {
        const amount = Number(values.amount);

        const payload = {
            staffId: selectedData?.staffId,
            amount,
            remarks: values.remarks?.trim() || "",
        };

        withdrawFundMutate(payload);
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
            }}
        >
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    Withdraw Funds from Wallet
                </h1>

                <button type="button" onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <ModalProfileHeader
                title="Staff Details"
                name={capitalizeFirstLetter(selectedData?.name)}
                subtitle={`#${selectedData?.mobile}`}
                amountLabel={"Current Balance"}
                amount={selectedData?.balance}
                // image={selectedData?.profilePicture}
                onClose={handleClose}
            />

            <div className="custom-scrollbar px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({
                        errors,
                        touched,
                        isSubmitting,
                        values,
                        setFieldValue,
                    }) => {
                        const withdrawAmount = Number(values.amount || 0);

                        const balanceAfterWithdraw =
                            walletBalance - withdrawAmount;

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100vh-140px)]">
                                    <Wrapper>
                                        {/* Transfer Amount */}
                                        <InputGroup>
                                            <Label htmlFor="mobileNo">Withdraw Amount *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="tel"
                                                    name="amount"
                                                    id="amount"
                                                    autoComplete="off"
                                                    maxLength="10"
                                                    max={walletBalance}
                                                    placeholder="Enter wallet balance"
                                                    className={errors.amount && touched.amount ? "border-red-500" : ""}
                                                />
                                            </FieldWrappers>
                                            {errors.amount && touched.amount && (
                                                <ErrorText>{errors.amount}</ErrorText>
                                            )}
                                        </InputGroup>

                                        {/* Balance After Transfer */}
                                        {withdrawAmount > 0 && (
                                            <InputGroup>
                                                <Label>
                                                    Wallet Balance After Withdraw
                                                </Label>

                                                <InfoBox>
                                                    <span className="text-lg font-semibold text-green-600">
                                                        ₹
                                                        {balanceAfterWithdraw.toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </span>
                                                </InfoBox>
                                            </InputGroup>
                                        )}


                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn
                                        type="button"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </SideCancelBtn>

                                    <SubmitBtn
                                        type="submit"
                                        disabled={
                                            isSubmitting ||
                                            withdrawFundLoading
                                        }
                                    >
                                        {isSubmitting || withdrawFundLoading
                                            ? "Withdrawing..."
                                            : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`
    bg-white
    w-full
    h-full
    flex
    flex-col
    relative
`;

const InfoBox = tw.div`
    w-full
    px-3
    py-2.5
    rounded-md
    bg-gray-50
    border
    border-gray-100
`;

const ErrorText = tw.div`
    text-red-500
    text-sm
    ml-0.5
    mt-1
`;

export default WithdrawFundFromWallet;